import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
const available = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("LocalNotifications");
const channelId = "season-updates";
const namespace = "lift-local-v1";
export type LocalNotice = { key: string; title: string; body: string; data: Record<string, unknown> };
// Stable IDs replace retries of an event instead of accumulating duplicates.
export function localNoticeId(key: string): number {
  let hash = 2166136261;
  for (const character of `${namespace}:${key}`) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return (hash >>> 1) || 1;
}
// Capacitor proxies expose every property, including `then`. Never resolve a
// Promise with a bare plugin proxy: Promise assimilation would call native `then`
// and hang notification setup. Return the module/container instead.
async function client() { return import("@capacitor/local-notifications"); }
async function prepare() {
  if (!available()) return null;
  const { LocalNotifications: notifications } = await client();
  // Never prompt for permission because a push arrived.
  if ((await notifications.checkPermissions()).display !== "granted") return null;
  if (Capacitor.getPlatform() === "android") await notifications.createChannel({ id: channelId, name: "Season updates", importance: 4, visibility: 0 });
  return { notifications };
}
export async function presentForegroundNotice(notice: LocalNotice): Promise<boolean> {
  if (Capacitor.getPlatform() !== "android") return false;
  const prepared = await prepare();
  if (!prepared) return false;
  const { notifications } = prepared;
  await notifications.schedule({ notifications: [{ id: localNoticeId(notice.key), title: notice.title, body: notice.body, channelId, extra: { ...notice.data, localNamespace: namespace } }] });
  return true;
}
/** Explicit one-off device reminders only. Never mirror server commitment
 * reminders here. Android scheduling is inexact; no exact-alarm permission. */
export async function scheduleDeviceReminder(notice: LocalNotice, at: Date): Promise<boolean> {
  if (!Number.isFinite(at.getTime()) || at.getTime() <= Date.now()) throw new Error("Choose a future reminder time.");
  const prepared = await prepare();
  if (!prepared) return false;
  const { notifications } = prepared;
  await notifications.schedule({ notifications: [{ id: localNoticeId(notice.key), title: notice.title, body: notice.body, channelId, schedule: { at }, extra: { ...notice.data, localNamespace: namespace } }] });
  return true;
}
export async function cancelDeviceReminder(key: string): Promise<void> {
  if (available()) await (await client()).LocalNotifications.cancel({ notifications: [{ id: localNoticeId(key) }] });
}
export async function clearLocalNotices(): Promise<void> {
  if (!available()) return;
  const { LocalNotifications: notifications } = await client();
  const pending = await notifications.getPending();
  await notifications.cancel({ notifications: pending.notifications.filter(n => n.extra?.localNamespace === namespace).map(n => ({ id: n.id })) });
  const delivered = await notifications.getDeliveredNotifications();
  await notifications.removeDeliveredNotifications({ notifications: delivered.notifications.filter(n => n.extra?.localNamespace === namespace) });
}
export async function watchLocalNoticeActions(onTap: (data: Record<string, unknown>) => void): Promise<PluginListenerHandle | null> {
  if (!available()) return null;
  return (await client()).LocalNotifications.addListener("localNotificationActionPerformed", event => {
    const extra = event.notification.extra;
    if (extra && typeof extra === "object" && extra.localNamespace === namespace) onTap(extra);
  });
}

const badgesAvailable = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Badge");
/** Use an authoritative unread total, never the size of a paginated inbox.
 * No prompt: notification preferences own the permission request. A zero count
 * also clears delivered notifications on iOS (the plugin's documented behavior).
 */
export async function setAppBadge(count: number): Promise<boolean> {
  if (!Number.isSafeInteger(count) || count < 0) throw new Error("Unread count must be a nonnegative integer.");
  if (!badgesAvailable()) return false;
  const { Badge } = await import("@capawesome/capacitor-badge");
  if (!(await Badge.isSupported()).isSupported || (await Badge.checkPermissions()).display !== "granted") return false;
  await Badge.set({ count });
  return true;
}
/** Account exit must remove the prior account's badge and delivered iOS notices. */
export async function clearAppBadge(): Promise<void> {
  if (!badgesAvailable()) return;
  const { Badge } = await import("@capawesome/capacitor-badge");
  if ((await Badge.isSupported()).isSupported) await Badge.clear();
}
